/**
 * Settings ▸ API Keys — mint, list, and revoke tenant API keys
 * (shared-auth ADR-007). The primary consumer is the SystemDox MCP
 * server (Build ▸ Connect), which authenticates with a key via
 * SYSTEMDOX_API_KEY. Role-gated like Members: viewers/editors see the
 * list read-only; mint/revoke are owner/admin (the API 403s them
 * anyway). The plaintext key is shown exactly once after minting.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  Copy,
  Key,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { Spinner, LoadingState } from '@systemdox/ui';
import { useTenant } from '@/contexts/TenantContext';
import { SignInAgainButton } from '@/contexts/AuthContext';
import {
  ALL_SPACES,
  DEFAULT_KEY_ACCESS,
  describeKeyAccess,
  keyAccessRequest,
  presetCanWrite,
  type KeyAccessChoice,
} from '@/lib/apiKeyAccess';
import { presetLabel } from '@/lib/grants';
import { usePresets } from '@/lib/usePresets';
import { spacesApi, type Space } from '@/services/spacesApi';
import { apiKeysApi, type ApiKeyMetadata, type CreatedApiKey } from '@/services/apiKeysApi';
import { problemCode, problemDetail } from '@/services/tenantApi';
import { cn } from '@/lib/utils';

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Why the API refused to mint: the two halves of AAL2 ([ADR-061] §10). */
export type StepUpReason = 'step_up_required' | 'aal2_required';

/** What each refusal asks of the person, in place of the API's own detail. */
export const STEP_UP_MESSAGE: Record<StepUpReason, string> = {
  step_up_required:
    'For security, creating a key needs a sign-in from the last 5 minutes. Sign in again, then create the key straight away.',
  aal2_required:
    'Creating a key needs a sign-in that used your authenticator code. Sign in again with your email and password, and enter the code when asked.',
};

function PlaintextReveal({ created, onDismiss }: { created: CreatedApiKey; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — the user can still select the text manually.
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
          <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
            Copy your key now — it will not be shown again
          </h3>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          title="Dismiss"
          className="rounded p-1 text-amber-500 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
        {created.name} — SystemDox stores only a hash. If you lose this key, revoke it and mint a new one.
      </p>
      <div className="mt-3 flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-3 font-mono text-sm text-slate-200 overflow-x-auto">
        <code className="flex-1 whitespace-nowrap select-all">{created.key}</code>
        <button
          type="button"
          onClick={copy}
          title="Copy to clipboard"
          className="shrink-0 rounded-md p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700/60 transition-colors"
        >
          {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export default function ApiKeysTab() {
  const { tenant } = useTenant();
  const canManage = tenant?.role === 'owner' || tenant?.role === 'admin';

  const [keys, setKeys] = useState<ApiKeyMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  // What the key may do: a catalogue preset, a space, and whether it may
  // change content (lib/apiKeyAccess). Read-only Reader on every space unless
  // the person chooses more; the server caps it to their own grants anyway.
  const [access, setAccess] = useState<KeyAccessChoice>(DEFAULT_KEY_ACCESS);
  const { presets } = usePresets();
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);

  const [busyId, setBusyId] = useState<string | null>(null);
  // Minting is AAL2 ([ADR-061] §10): a sign-in within 5 minutes that answered
  // a second factor. The API says which half failed; both are fixed by signing
  // in again, so the page offers that instead of printing the refusal: the
  // shared SignInAgainButton (Amendment 18) signs out of SystemDox only, opens
  // the in-app form (password and code) and comes back here.
  const [stepUp, setStepUp] = useState<StepUpReason | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setKeys(await apiKeysApi.list());
      setError(null);
    } catch (err) {
      setError(problemDetail(err, 'Could not load API keys.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    // The space names for the picker and the list. A failure leaves only
    // "All spaces" and raw ids: the key can still be made.
    spacesApi.listSpaces().then((list) => setSpaces(list.filter((sp) => !sp.archivedAt)), () => setSpaces([]));
  }, []);

  const spaceName = (spaceId: string) => spaces.find((sp) => sp.spaceId === spaceId)?.name ?? spaceId;
  const canWrite = !!presets && presetCanWrite(access.preset, presets);

  const handleCreate = async () => {
    if (!name.trim() || creating || !presets) return;
    setCreating(true);
    setError(null);
    try {
      const result = await apiKeysApi.create(name.trim(), keyAccessRequest(access, presets));
      setCreated(result);
      setKeys((prev) => [...prev, result]);
      setName('');
      setAccess(DEFAULT_KEY_ACCESS);
      setShowForm(false);
    } catch (err) {
      const code = problemCode(err);
      if (code === 'step_up_required' || code === 'aal2_required') setStepUp(code);
      else setError(problemDetail(err, 'Could not create the API key.'));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key: ApiKeyMetadata) => {
    if (busyId) return;
    setBusyId(key.keyId);
    setError(null);
    try {
      await apiKeysApi.revoke(key.keyId);
      const revokedAt = new Date().toISOString();
      setKeys((prev) => prev.map((k) => (k.keyId === key.keyId ? { ...k, revokedAt } : k)));
      setConfirmRevoke(null);
      if (created?.keyId === key.keyId) setCreated(null);
    } catch (err) {
      setError(problemDetail(err, 'Could not revoke the API key.'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <LoadingState size="lg" label="Loading API keys..." />
    );
  }

  const activeKeys = keys.filter((k) => !k.revokedAt);
  const revokedKeys = keys.filter((k) => k.revokedAt);

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">API Keys</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 max-w-xl">
            Tenant-scoped keys that authenticate API clients — most importantly the{' '}
            <Link to="/settings/connect" className="text-blue-600 dark:text-blue-400 hover:underline">
              SystemDox MCP server
            </Link>{' '}
            (set as <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">SYSTEMDOX_API_KEY</code>).
          </p>
        </div>
        {canManage && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 shrink-0"
          >
            <Plus className="w-4 h-4" />
            New Key
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {stepUp && (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200"
        >
          <AlertCircle className="w-4 h-4 shrink-0" />
          <p className="flex-1 min-w-0">{STEP_UP_MESSAGE[stepUp]}</p>
          <SignInAgainButton className="shrink-0" />
        </div>
      )}

      {created && <PlaintextReveal created={created} onDismiss={() => setCreated(null)} />}

      {showForm && (
        <div className="mb-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
            New API key
          </h3>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder="Key name, e.g. “Claude Code — laptop”"
              maxLength={100}
              autoFocus
              className="flex-1 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 focus:border-blue-500 focus:outline-none"
            />
            <button
              onClick={handleCreate}
              disabled={!name.trim() || creating || !presets}
              className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? <Spinner size="sm" tone="current" /> : <Key className="w-4 h-4" />}
              Create
            </button>
            <button
              onClick={() => { setShowForm(false); setName(''); setAccess(DEFAULT_KEY_ACCESS); }}
              disabled={creating}
              className="px-3 py-2 rounded-md text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Cancel
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Name the key after where it will live — per developer or per machine — so
            revoking one later doesn't disrupt the others.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">
              Access
              <select
                value={access.preset}
                onChange={(e) => setAccess((a) => ({ ...a, preset: e.target.value }))}
                disabled={!presets || creating}
                className="mt-1 block w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-700 dark:text-slate-300"
              >
                {(presets ?? []).map((p) => (
                  <option key={p.slug} value={p.slug}>{p.label} — {p.description}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">
              Space
              <select
                value={access.space}
                onChange={(e) => setAccess((a) => ({ ...a, space: e.target.value }))}
                disabled={creating}
                className="mt-1 block w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-700 dark:text-slate-300"
              >
                <option value={ALL_SPACES}>All spaces</option>
                {spaces.map((sp) => (
                  <option key={sp.spaceId} value={sp.spaceId}>{sp.name}</option>
                ))}
              </select>
            </label>
          </div>
          <label className={cn('mt-3 flex items-start gap-2 text-sm', canWrite ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400')}>
            <input
              type="checkbox"
              checked={access.write && canWrite}
              onChange={(e) => setAccess((a) => ({ ...a, write: e.target.checked }))}
              disabled={!canWrite || creating}
              className="mt-0.5"
            />
            <span>
              Allow changes — the key may create and edit content (checks, prompt templates, specs).
              {presets && !canWrite && ` ${presetLabel(access.preset, presets)} only reads.`}
              {' '}Off, the key only reads. Either way it never gets more than you have.
            </span>
          </label>
        </div>
      )}

      {activeKeys.length === 0 && !showForm ? (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 text-center">
          <Key className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-700" />
          <h3 className="mt-4 font-semibold text-slate-900 dark:text-slate-100">No API keys yet</h3>
          <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
            {canManage
              ? 'Mint a key to connect your AI tools to SystemDox through the MCP server.'
              : 'An owner or admin of this workspace can mint API keys.'}
          </p>
          {canManage && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              New Key
            </button>
          )}
        </div>
      ) : (
        activeKeys.length > 0 && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 text-left">
                  <th className="p-3 font-medium text-slate-500">Name</th>
                  <th className="p-3 font-medium text-slate-500">Key</th>
                  <th className="p-3 font-medium text-slate-500">Access</th>
                  <th className="p-3 font-medium text-slate-500">Created</th>
                  <th className="p-3 font-medium text-slate-500">Last used</th>
                  {canManage && <th className="p-3" />}
                </tr>
              </thead>
              <tbody>
                {activeKeys.map((key) => (
                  <tr
                    key={key.keyId}
                    className="border-b border-slate-100 dark:border-slate-800/60 last:border-0"
                  >
                    <td className="p-3 font-medium text-slate-900 dark:text-slate-100">{key.name}</td>
                    <td className="p-3 font-mono text-xs text-slate-500">…{key.last4}</td>
                    <td className="p-3 text-xs text-slate-500">{describeKeyAccess(key, presets ?? [], spaceName)}</td>
                    <td className="p-3 text-slate-500">{formatDate(key.createdAt)}</td>
                    <td className="p-3 text-slate-500">{key.lastUsedAt ? formatDate(key.lastUsedAt) : 'Never'}</td>
                    {canManage && (
                      <td className="p-3 text-right">
                        {confirmRevoke === key.keyId ? (
                          <span className="inline-flex items-center gap-2">
                            <button
                              onClick={() => handleRevoke(key)}
                              disabled={busyId !== null}
                              className="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                            >
                              {busyId === key.keyId ? (
                                <Spinner size="sm" tone="current" aria-label="Revoking key" />
                              ) : (
                                'Confirm revoke'
                              )}
                            </button>
                            <button
                              onClick={() => setConfirmRevoke(null)}
                              disabled={busyId !== null}
                              className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button
                            onClick={() => setConfirmRevoke(key.keyId)}
                            title="Revoke key"
                            className={cn(
                              'rounded p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50',
                              'dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors',
                            )}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {revokedKeys.length > 0 && (
        <div className="mt-6">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Revoked
          </h3>
          <div className="space-y-1">
            {revokedKeys.map((key) => (
              <div
                key={key.keyId}
                className="flex items-center gap-3 rounded-lg border border-slate-100 dark:border-slate-800/60 px-3 py-2 text-sm text-slate-400 dark:text-slate-500"
              >
                <span className="line-through">{key.name}</span>
                <span className="font-mono text-xs">…{key.last4}</span>
                <span className="ml-auto text-xs">revoked {formatDate(key.revokedAt)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
